/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ReferencedComponent from '../Utility/Component/ComponentReference/ReferencedComponent';
import AudioSource from './AudioSource';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Repesents a pairing of an [[cc.AudioSource]] and a cue.
 */
@cc._decorator.ccclass
export default class AudioTrigger extends ReferencedComponent
{
    /** @hidden */
    @property({ visible: true })
    private _audioTriggerID: string = "";
    /** The cue that should fire this trigger. */
    public get AudioTriggerID(): string { return this._audioTriggerID; }

    /** The [[cc.AudioSource]] that this trigger should play when fired. */
    @property({ type: cc.AudioSource, visible: true })
    protected _audioSource: cc.AudioSource = null;

    /** Optional duration poperty for bangup calculations. */
    @property(cc.Float)
    public Duration: number = 0;

    /** The [[cc.AudioSource]] that this trigger should play when fired. */
    @property()
    protected noInterrupt: boolean = false;

    /** Whether or not the associated [[cc.AudioSource]] is currently playing. */
    protected get isPlaying(): boolean { return this._audioSource.isPlaying; }

    /** @hidden */
    protected isStarted: boolean = false;

    /** @callback OnComplete Will fire when the associated [[cc.AudioSource]] finishes playing. */
    public OnComplete: Function = null;

    protected audioSourceVolume: number = 0;

    protected isPaused: boolean = false;

    protected onLoad(): void
    {
        if (this._audioSource)
        {
            // tslint:disable-next-line:no-string-literal
            this._audioSource["preload"] = this._audioSource["preload"];
        }

        super.onLoad();
        this.configureStartVolume();
    }

    /**
     * Sets volume percent.
     * @param percent percent volume to set to.
     */
    public SetVolume(percent: number): void
    {
        if (!this._audioSource) { return; }

        const newVolume: number = this.audioSourceVolume * percent;
        this._audioSource.volume = newVolume;

        if (this._audioSource instanceof AudioSource)
        {
            (this._audioSource as AudioSource).FullVolume = newVolume;
        }
    }

    /**
     * Sets initial start volume from source.
     */
    protected configureStartVolume(): void
    {
        if (!this._audioSource) { return; }
        this.audioSourceVolume = this._audioSource.volume;
    }

    /** @hidden */
    protected update(dt: number): void
    {
        this.onCompleteHandler();
    }

    private onCompleteHandler(): void
    {
        if (!this.isStarted || this.isPlaying !== false || this.isPaused) { return; }

        if (this.OnComplete) { this.OnComplete(); }
        this.isStarted = false;
    }

    /** Stops the associated [[cc.AudioSource]] if it is playing. */
    public Stop(): void
    {
        if (!this._audioSource) { return; }
        this._audioSource.stop();
    }

    public Pause(): void
    {
        if (!this._audioSource) { return; }
        this._audioSource.pause();
        this.isPaused = true;
    }

    public Resume(): void
    {
        if (!this._audioSource || !this.isPaused) { return; }
        this._audioSource.resume();
        this.isPaused = false;
    }

    /** Plays the associated [[cc.AudioSource]]. */
    public Trigger(): void
    {
        if (this._audioSource.isPlaying)
        {
            if (this.noInterrupt) { return; }

            this._audioSource.stop();
        }
        this.playAudioSource(this._audioSource);
        this.isStarted = true;
    }

    protected playAudioSource(source: cc.AudioSource): void
    {
        source.volume = source.volume; // Workaround for CC bug.
        source.loop = source.loop; // Workaround for CC bug.
        source.mute = source.mute;
        source.play();
    }
}
